/**
 * Chart Bar Scaling Utilities
 *
 * Provides scaling functions for bar charts that need to visualize data
 * with large value ranges while preserving visual distinction between values.
 */

export type BarScaleMode = "linear" | "log" | "hybrid" | "sqrt";

export interface BarScaleOptions {
  mode?: BarScaleMode;
  minWidthPct?: number;
  maxWidthPct?: number;
  hybridThreshold?: number;
}

const DEFAULT_OPTIONS: Required<BarScaleOptions> = {
  mode: "hybrid",
  minWidthPct: 2,
  maxWidthPct: 100,
  hybridThreshold: 100,
};

/**
 * Calculates bar width percentage using a hybrid linear-logarithmic scale.
 *
 * For data with extreme ranges (e.g., 4M vs 2K), pure linear scaling makes
 * small values invisible. Pure log scaling compresses large differences too much.
 *
 * This hybrid approach:
 * - Uses linear scaling when the max/min ratio is small (< threshold)
 * - Switches to sqrt scaling for moderate ranges
 * - Uses a compressed log scale for extreme ranges
 *
 * This ensures visual distinction while maintaining relative relationships.
 */
export function calculateBarWidthPct(
  value: number,
  maxValue: number,
  minValue: number,
  options: BarScaleOptions = {},
): number {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { minWidthPct, maxWidthPct, hybridThreshold, mode } = opts;

  if (maxValue <= 0) {return 0;}
  if (value <= 0) {return 0;}

  const effectiveMin = Math.max(minValue, 0.001);
  const range = maxValue / effectiveMin;

  let scaledValue: number;

  if (mode === "linear" || range < hybridThreshold) {
    scaledValue = value / maxValue;
  } else if (mode === "sqrt" || (mode === "hybrid" && range < hybridThreshold * 10)) {
    scaledValue = Math.sqrt(value) / Math.sqrt(maxValue);
  } else if (mode === "log") {
    scaledValue = logScale(value, effectiveMin, maxValue);
  } else {
    scaledValue = hybridScale(value, effectiveMin, maxValue, range);
  }

  const widthPct = scaledValue * (maxWidthPct - minWidthPct) + minWidthPct;
  return Math.max(minWidthPct, Math.min(maxWidthPct, widthPct));
}

function logScale(value: number, minValue: number, maxValue: number): number {
  const logMin = Math.log10(Math.max(minValue, 1));
  const logMax = Math.log10(maxValue);
  const logVal = Math.log10(Math.max(value, 1));

  if (logMax === logMin) {return 1;}
  return (logVal - logMin) / (logMax - logMin);
}

function hybridScale(
  value: number,
  minValue: number,
  maxValue: number,
  range: number,
): number {
  const compressionFactor = Math.log10(range) / 4;
  const power = 1 / (1 + compressionFactor);
  return Math.pow(value / maxValue, power);
}

/**
 * Pre-calculates bar widths for an array of values.
 * Returns width percentages that maintain visual distinction.
 */
export function calculateBarWidths(
  values: number[],
  options: BarScaleOptions = {},
): number[] {
  if (values.length === 0) {return [];}

  const positiveValues = values.filter(v => v > 0);
  if (positiveValues.length === 0) {
    return values.map(() => 0);
  }

  const maxValue = Math.max(...positiveValues);
  const minValue = Math.min(...positiveValues);

  return values.map(value =>
    calculateBarWidthPct(value, maxValue, minValue, options),
  );
}

/**
 * Returns appropriate scale mode based on data characteristics.
 */
export function suggestScaleMode(values: number[]): BarScaleMode {
  const positiveValues = values.filter(v => v > 0);
  if (positiveValues.length < 2) {return "linear";}

  const max = Math.max(...positiveValues);
  const min = Math.min(...positiveValues);
  const range = max / min;

  if (range < 10) {return "linear";}
  if (range < 100) {return "sqrt";}
  if (range < 10000) {return "hybrid";}
  return "log";
}

/**
 * Formats axis ticks for non-linear scales.
 * Returns tick values that are evenly spaced visually.
 */
export function generateScaledAxisTicks(
  minValue: number,
  maxValue: number,
  tickCount: number = 3,
  mode: BarScaleMode = "hybrid",
): number[] {
  if (maxValue <= minValue || tickCount < 2) {
    return [minValue, maxValue].filter(v => v >= 0);
  }

  const ticks: number[] = [];

  if (mode === "linear") {
    const step = (maxValue - minValue) / (tickCount - 1);
    for (let i = 0; i < tickCount; i++) {
      ticks.push(minValue + step * i);
    }
  } else if (mode === "log") {
    const logMin = Math.log10(Math.max(minValue, 1));
    const logMax = Math.log10(maxValue);
    const logStep = (logMax - logMin) / (tickCount - 1);
    for (let i = 0; i < tickCount; i++) {
      ticks.push(Math.pow(10, logMin + logStep * i));
    }
  } else {
    ticks.push(0);
    const midpoints = tickCount - 2;
    for (let i = 1; i <= midpoints; i++) {
      const ratio = i / (midpoints + 1);
      const tickValue = maxValue * Math.pow(ratio, 2);
      ticks.push(tickValue);
    }
    ticks.push(maxValue);
  }

  return ticks.map(v => Math.round(v));
}
